import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceDot, ResponsiveContainer } from 'recharts';
import { Text } from '../../../components/ui/typography/Text';

interface ResultsGraphProps {
  userInitialConfidence: string;
  userScore: number;
}

interface DataPoint {
  competence: number;
  confidence: number;
  label: string;
  position: string;
  stage: string;
}

interface PositionConfig {
  offset: number;
  position: string;
}

const graphData: DataPoint[] = [
  { competence: 0, confidence: 20, label: 'Know-nothing', position: 'right', stage: 'Start of Learning' },
  { competence: 10, confidence: 80, label: 'Peak of "Mount Stupid"', position: 'top', stage: 'Early Overconfidence' },
  { competence: 30, confidence: 15, label: 'Valley of Despair', position: 'bottom', stage: 'Reality Sets In' },
  { competence: 60, confidence: 50, label: 'Slope of Enlightenment', position: 'right', stage: 'Growing Understanding' },
  { competence: 100, confidence: 75, label: 'Plateau of Sustainability', position: 'left', stage: 'Expert Level' }
];

const desktopPositions = new Map<string, PositionConfig>([
  ['Know-nothing', { offset: 15, position: 'right' }],
  ['Peak of "Mount Stupid"', { offset: 25, position: 'right' }],
  ['Valley of Despair', { offset: 20, position: 'bottom' }],
  ['Slope of Enlightenment', { offset: 25, position: 'right' }],
  ['Plateau of Sustainability', { offset: 20, position: 'left' }]
]);

const mobilePositions = new Map<string, PositionConfig>([
  ['Know-nothing', { offset: 15, position: 'right' }],
  ['Peak of "Mount Stupid"', { offset: 10, position: 'right' }],
  ['Valley of Despair', { offset: 10, position: 'left' }],
  ['Slope of Enlightenment', { offset: 25, position: 'right' }],
  ['Plateau of Sustainability', { offset: 20, position: 'left' }]
]);

export const ResultsGraph: React.FC<ResultsGraphProps> = ({
  userInitialConfidence,
  userScore,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const chartRef = useRef<any>(null);
  
  // Parse the confidence value
  const userConfidenceValue = parseFloat(userInitialConfidence) || 0;
  
  // Ensure userScore is a number and within bounds
  const safeUserScore = typeof userScore === 'number' ? 
    Math.min(Math.max(userScore, 0), 100) : 0;
  
  // Create a data point for the user's assessment
  const userDataPoint = {
    x: safeUserScore,
    y: userConfidenceValue
  };

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Add user data to tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2 border border-gray-200 rounded-md shadow-sm">
          <p className="font-medium text-xs">{`Knowledge: ${label}%`}</p>
          <p className="text-blue-600 text-xs">{`Confidence: ${payload[0].value.toFixed(1)}%`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <Text className="font-medium text-gray-900">The Dunning-Kruger Effect</Text>
        <Text className="text-gray-600">
          This graph illustrates how our perception of ability changes as we gain actual knowledge.
          Your initial assessment is marked with an orange dot.
        </Text>
      </div>
      
      <div className={`bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100 p-4 ${isMobile ? 'overflow-x-auto' : ''}`}>
        <div className={`w-full h-80 ${isMobile ? 'min-w-[500px]' : ''}`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              ref={chartRef}
              data={graphData}
              margin={{ top: 30, right: 80, left: 20, bottom: 30 }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                horizontal={true}
                vertical={false}
                stroke="#e5e7eb"
              />
              <XAxis
                dataKey="competence"
                type="number"
                domain={[0, 100]}
                label={{ 
                  value: 'Knowledge - Experience', 
                  position: 'bottom',
                  offset: 15,
                  fill: '#6b7280',
                  fontSize: 12
                }}
                tickCount={6}
                stroke="#9ca3af"
                tick={{ fontSize: 11 }}
              />
              <YAxis
                domain={[0, 100]}
                label={{ 
                  value: 'Confidence', 
                  angle: -90, 
                  position: 'left',
                  offset: 0,
                  fill: '#6b7280',
                  fontSize: 12
                }}
                tickCount={6}
                stroke="#9ca3af"
                tick={{ fontSize: 11 }}
              />
              {!isMobile && <Tooltip content={<CustomTooltip />} />}
              <Line
                type="monotone"
                dataKey="confidence"
                stroke="#2563eb"
                strokeWidth={2.5}
                dot={(props) => {
                  const { payload, cx, cy, index } = props;
                  const dataPoint = payload as DataPoint;
                  const positions = isMobile ? mobilePositions : desktopPositions;
                  const positionConfig = positions.get(dataPoint.label) || { offset: 15, position: 'right' };
                  
                  let labelX = cx;
                  let labelY = cy;
                  let textAnchor = 'middle';

                  switch (positionConfig.position) {
                    case 'top':
                      labelY = cy - positionConfig.offset;
                      break;
                    case 'bottom':
                      labelY = cy + positionConfig.offset;
                      break;
                    case 'right':
                      labelX = cx + positionConfig.offset;
                      textAnchor = 'start';
                      break;
                    case 'left':
                      labelX = cx - positionConfig.offset;
                      textAnchor = 'end';
                      break;
                  }

                  return (
                    <g>
                      <circle cx={cx} cy={cy} r={3.5} fill="#2563eb" />
                      <text
                        x={labelX}
                        y={labelY}
                        textAnchor={textAnchor}
                        fill="#4b5563"
                        fontSize={11}
                        fontWeight="500"
                      >
                        {isMobile ? `${index + 1}` : dataPoint.label}
                      </text>
                    </g>
                  );
                }}
              />
              {/* Add a custom reference dot for the user's assessment */}
              <ReferenceDot
                x={safeUserScore}
                y={userConfidenceValue}
                r={5}
                fill="#f97316"
                stroke="white"
                strokeWidth={1.5}
                label={{ 
                  value: "Your Assessment",
                  position: 'top',
                  fill: '#f97316',
                  fontSize: 11,
                  fontWeight: 'medium',
                  offset: 8
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {isMobile && (
        <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100 py-2 px-3">
          <div className="flex flex-col space-y-1">
            {graphData.map((point, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="flex-shrink-0 flex items-center justify-center w-4 h-4 rounded-full bg-blue-100 text-blue-600 text-xs font-bold">{index + 1}</span>
                <Text className="text-xs text-gray-700 leading-tight">{point.label}</Text>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3 text-sm text-gray-700">
        <Text>
          <span className="font-medium">Key Findings:</span> The bottom quartile scored at the 12th percentile 
          but estimated themselves at the 68th percentile. Top performers were more accurate but slightly 
          underestimated their performance.
        </Text>
        <Text>
          <span className="font-medium">The Graph:</span> While the popular "Mount Stupid" curve shown above 
          wasn't part of the original study, it's become a useful way to visualize how confidence and 
          competence typically relate as someone gains expertise in a field.
        </Text>
      </div>
    </div>
  );
}; 